import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, GraduationCap, Loader2, LogOut } from 'lucide-react';
import { Logo, LanguageToggle, CardCorner, Button } from '../ui';
import { useAuthStore } from '../../store/useAuthStore';
import { ApiError } from '../../services/api/apiClient';
import becasApiService, {
  type BecasDossier,
  type BecasDossierStatus,
  type BecasPaymentInfo,
  type BecasProfile,
} from '../../services/api/becasApiService';
import { getPaymentCatalog } from '../../services/api/paymentCatalogService';
import { detectBrowserCountry } from '../../utils/detectCountry';
import { BecasAuthForm } from './BecasAuthForm';
import { BecasProfileForm } from './BecasProfileForm';
import { BecasDocumentsForm } from './BecasDocumentsForm';
import { BecasPaymentForm } from './BecasPaymentForm';
import { BecasSubmitPanel } from './BecasSubmitPanel';

const STEPS: BecasDossierStatus[] = [
  'profile_incomplete',
  'documents_incomplete',
  'ready_to_pay',
  'paid',
  'submitted',
];

function defaultPayment(country: string): BecasPaymentInfo {
  if (country === 'CL') {
    return { amount: 49990, currency: 'CLP', locale: 'es-CL', plan_name: 'Becas Deportivas' };
  }
  return { amount: 4990, currency: 'USD', locale: 'en-US', plan_name: 'Sports Scholarships' };
}

export function BecasWizard() {
  const { t } = useTranslation();
  const { user, isAuthenticated, hydrate, logout } = useAuthStore();
  const [hydrated, setHydrated] = useState(false);
  const [dossier, setDossier] = useState<BecasDossier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<BecasPaymentInfo | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | undefined>(
    () => import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  );

  useEffect(() => {
    hydrate();
    setHydrated(true);
  }, [hydrate]);

  const loadDossier = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await becasApiService.getDossier();
      setDossier(next);
      const key = next.publishable_key || next.payment?.publishable_key;
      if (key) setPublishableKey(key);
      return next;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        await logout();
        setDossier(null);
      } else {
        setError(err instanceof ApiError ? err.message : t('becas.errors.generic'));
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [logout, t]);

  useEffect(() => {
    if (!hydrated || !isAuthenticated) return;
    void loadDossier();
  }, [hydrated, isAuthenticated, loadDossier]);

  const status: BecasDossierStatus = dossier?.status ?? 'profile_incomplete';

  useEffect(() => {
    if (!isAuthenticated || status !== 'ready_to_pay') return;
    const country = detectBrowserCountry();
    void getPaymentCatalog(country)
      .then((catalog) => {
        const plan = catalog.app_plans?.find((item) => item.slug === 'becas') ?? catalog.app_plans?.[0];
        setPaymentInfo({
          amount: plan?.price ?? defaultPayment(catalog.country.code).amount,
          currency: plan?.currency || catalog.country.currency,
          locale: catalog.country.locale,
          plan_name: plan?.name,
          plan_id: plan?.id,
        });
      })
      .catch(() => {
        setPaymentInfo(defaultPayment(country));
      });
  }, [isAuthenticated, status]);

  const saveProfile = async (profile: BecasProfile) => {
    const next = await becasApiService.saveProfile(profile);
    setDossier(next);
  };

  const saveDocuments = async (fileId: string) => {
    const next = await becasApiService.saveDocuments(fileId);
    setDossier(next);
  };

  const confirmPay = async (paymentToken?: string, discountCode?: string) => {
    setPaying(true);
    setPayError(null);
    try {
      const next = await becasApiService.pay({
        provider: paymentToken ? 'stripe' : 'free',
        payment_token: paymentToken,
        discount_code: discountCode,
      });
      setDossier(next);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('becas.errors.payment');
      setPayError(message);
      throw err;
    } finally {
      setPaying(false);
    }
  };

  const submitApplication = async () => {
    const next = await becasApiService.submit();
    setDossier(next.status ? next : { ...dossier, status: 'submitted' });
  };

  const stepIndex = useMemo(() => {
    if (!isAuthenticated) return 0;
    return STEPS.indexOf(status) + 1;
  }, [isAuthenticated, status]);

  return (
    <div className="min-h-screen bg-brand-darkNavy text-white flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,215,0,0.04)_0%,transparent_60%)] pointer-events-none" />
      <header className="relative z-10 border-b border-brand-lightBlue/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <a href="/" aria-label="BTalentStar">
            <Logo />
          </a>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => void logout()}
                className="inline-flex items-center gap-2 text-sm text-dark-400 hover:text-brand-yellow transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('becas.logout')}
              </button>
            )}
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm text-dark-400 hover:text-brand-yellow transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('becas.back')}
            </a>
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center shrink-0">
              <GraduationCap className="w-6 h-6 text-brand-yellow" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black">{t('becas.title')}</h1>
              <p className="text-dark-400 text-sm">{t('becas.subtitle')}</p>
            </div>
          </div>

          <ol className="grid grid-cols-5 gap-2 mb-8">
            {STEPS.map((key, i) => {
              const active = stepIndex >= i + 1;
              return (
                <li key={key} className="text-center">
                  <div
                    className={`h-1.5 rounded-full mb-2 ${active ? 'bg-brand-yellow' : 'bg-brand-lightBlue/30'}`}
                  />
                  <span className={`text-[10px] sm:text-xs ${active ? 'text-brand-yellow' : 'text-dark-500'}`}>
                    {t(`becas.steps.${key}`)}
                  </span>
                </li>
              );
            })}
          </ol>

          <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-lightBlue/30 rounded-3xl p-6 md:p-8 overflow-hidden">
            <CardCorner position="top-left" color="gold" />
            <CardCorner position="bottom-right" color="gold" />

            {!hydrated && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
              </div>
            )}

            {hydrated && !isAuthenticated && (
              <>
                <p className="text-dark-400 text-sm mb-6">{t('becas.auth.intro')}</p>
                <BecasAuthForm />
              </>
            )}

            {hydrated && isAuthenticated && loading && !dossier && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-brand-yellow" />
              </div>
            )}

            {hydrated && isAuthenticated && error && (
              <div className="space-y-4">
                <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
                  {error}
                </p>
                <Button variant="outline" fullWidth onClick={() => void loadDossier()}>
                  {t('becas.retry')}
                </Button>
              </div>
            )}

            {hydrated && isAuthenticated && dossier && !error && status === 'profile_incomplete' && (
              <BecasProfileForm
                initial={dossier.profile}
                defaults={{ first_name: user?.first_name, last_name: user?.last_name }}
                onSaved={saveProfile}
              />
            )}

            {hydrated && isAuthenticated && dossier && !error && status === 'documents_incomplete' && (
              <BecasDocumentsForm initial={dossier.documents} onSaved={saveDocuments} />
            )}

            {hydrated && isAuthenticated && dossier && !error && status === 'ready_to_pay' && (
              <BecasPaymentForm
                payment={dossier.payment ?? paymentInfo}
                paying={paying}
                payError={payError}
                publishableKey={publishableKey}
                onPay={confirmPay}
              />
            )}

            {hydrated && isAuthenticated && dossier && !error && (status === 'paid' || status === 'submitted') && (
              <BecasSubmitPanel submitted={status === 'submitted'} onSubmit={submitApplication} />
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
